import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Avatar, Box, Button, CircularProgress, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { nodeMeta, ORBIT_COLORS, type NetworkNode, type NetworkView } from '@rhwb/types';
import { addFamily, dismissFamilySuggestion, fetchFamilySuggestions } from '../../lib/network';

interface FamilySectionProps {
  /** The root runner's family, the runner excluded. */
  family: NetworkNode[];
  /** Admin override view: members only, no changes or suggestions. */
  readOnly: boolean;
  /** "You", or the viewed runner's first name in the override view. */
  rootName: string;
  onOpen: (node: NetworkNode) => void;
  onAdd: () => void;
  onRemove: (node: NetworkNode) => void;
  /** The server's fresh view after an add. */
  onChanged: (view: NetworkView) => void;
}

const initials = (name: string) =>
  name.split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase();

/**
 * The Family part of the Amazing Network card: members (click to open, × to
 * remove), runners at your address offered as "Add" / "Not family", and
 * "Add family member". Families are added immediately — no request to accept.
 */
const FamilySection: React.FC<FamilySectionProps> = ({
  family,
  readOnly,
  rootName,
  onOpen,
  onAdd,
  onRemove,
  onChanged,
}) => {
  const [suggestions, setSuggestions] = useState<NetworkNode[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadSuggestions = useCallback(async () => {
    if (readOnly) return;
    try {
      setSuggestions(await fetchFamilySuggestions());
    } catch {
      setSuggestions([]); // A nicety; never block the card.
    }
  }, [readOnly]);

  // Reload when the family changes: a new member drops out of the list.
  const familyKey = family.map((f) => f.id).join(',');
  useEffect(() => {
    loadSuggestions();
  }, [loadSuggestions, familyKey]);

  const run = async (key: string, call: () => Promise<void>) => {
    setBusy(key);
    setError(null);
    try {
      await call();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Box
          sx={{
            width: 14,
            height: 14,
            borderRadius: '50%',
            bgcolor: ORBIT_COLORS.familyFill,
            border: `1px solid ${ORBIT_COLORS.familyStroke}`,
          }}
        />
        <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'text.secondary' }}>
          {readOnly ? `${rootName}'s family` : 'Your family'}
        </Typography>
      </Box>

      {family.length === 0 && readOnly && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          No family on record.
        </Typography>
      )}

      {family.map((member) => (
        <Box key={member.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            role="button"
            tabIndex={0}
            onClick={() => onOpen(member)}
            onKeyDown={(e) => e.key === 'Enter' && onOpen(member)}
            sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flex: 1, minWidth: 0, cursor: 'pointer', py: 0.5, borderRadius: 1, '&:hover': { bgcolor: 'rgba(0,0,0,0.03)' } }}
          >
            <Avatar src={member.picture ?? undefined} sx={{ width: 32, height: 32, fontSize: 12, fontWeight: 700, bgcolor: ORBIT_COLORS.family }}>
              {initials(member.name)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                {member.name}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap component="div">
                {nodeMeta(member)}
              </Typography>
            </Box>
          </Box>
          {!readOnly && (
            <IconButton size="small" onClick={() => onRemove(member)} aria-label={`Remove ${member.name} from your family`} sx={{ bgcolor: '#eef1f5' }}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </Box>
      ))}

      {!readOnly && suggestions.length > 0 && (
        <Box sx={{ mt: 0.5, pt: 1, borderTop: '1px solid #EEF0F3' }}>
          <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: 'text.secondary', mb: 0.5 }}>
            Lives at your address
          </Typography>
          {suggestions.map((s) => (
            <Box key={s.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
              <Avatar src={s.picture ?? undefined} sx={{ width: 32, height: 32, fontSize: 12, fontWeight: 700, bgcolor: ORBIT_COLORS.familyStroke }}>
                {initials(s.name)}
              </Avatar>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                  {s.name}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap component="div">
                  {nodeMeta(s)}
                </Typography>
              </Box>
              {busy === s.id ? (
                <CircularProgress size={18} sx={{ color: ORBIT_COLORS.family }} />
              ) : (
                <>
                  <Button
                    size="small"
                    onClick={() => run(s.id, async () => onChanged(await addFamily(s.id)))}
                    sx={{ textTransform: 'none', fontWeight: 700, color: ORBIT_COLORS.family, bgcolor: ORBIT_COLORS.familyFill, borderRadius: 999, px: 1.5, minWidth: 0 }}
                  >
                    Add
                  </Button>
                  <Button
                    size="small"
                    onClick={() =>
                      run(s.id, async () => {
                        await dismissFamilySuggestion(s.id);
                        setSuggestions((prev) => prev.filter((x) => x.id !== s.id));
                      })
                    }
                    sx={{ textTransform: 'none', color: 'text.secondary', minWidth: 0 }}
                  >
                    Not family
                  </Button>
                </>
              )}
            </Box>
          ))}
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 0.5 }}>
          {error}
        </Alert>
      )}

      {!readOnly && (
        <Button
          onClick={onAdd}
          startIcon={<Box component="span" sx={{ fontWeight: 800, fontSize: 18, lineHeight: 1 }}>+</Box>}
          sx={{ mt: 0.5, height: 44, borderRadius: '12px', border: `1.5px dashed ${ORBIT_COLORS.familyStroke}`, textTransform: 'none', fontWeight: 700, color: ORBIT_COLORS.family }}
        >
          Add family member
        </Button>
      )}
    </Box>
  );
};

export default FamilySection;
